import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BlankSessionService } from '@helios/sessions'
import { RootState } from '@helios/state-management'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { take } from 'rxjs/operators'
import {
  Break,
  BreakNoteHandling,
  CodeAvailability,
} from '../../model/break.model'
import {
  actionCodeOptions,
  breakDialogValidation,
  BreakDialogValidation,
  computeStaleCodeHints,
  descriptionCodeOptions,
  notesRequiredActionCodes,
  prefillSelection,
} from '../../validators/break-code-validation'
import { resolvePaste } from '../../model/break-code-paste'
import { isPasteBreakCodesEvent } from '../../utils/paste-break-codes-event'
import { BreakCodeClipboardService } from '../../services/break-code-clipboard.service'
import { MultiBreaksService } from '../../services/multi-breaks.service'
import { NoteEditorComponent } from '../note-editor/note-editor.component'
import { BREAK_DIALOG_SHARED_STYLES } from '../break-dialog-shared.styles'

@Component({
  selector: 'hs-resolve-modal',
  templateUrl: './resolve-modal.component.html',
  styles: [
    BREAK_DIALOG_SHARED_STYLES,
    `
      .modal-body {
        max-height: calc(65vh / var(--hs-ui-scale, 1));
        overflow-y: auto;
      }
      .modal-body ::ng-deep .ng-select,
      .modal-body ::ng-deep .ng-dropdown-panel .ng-option,
      .modal-body ::ng-deep textarea {
        font-size: 12px;
      }
      .field-row nf-form-control {
        margin-bottom: 0;
      }
    `,
  ],
})
export class ResolveModalComponent {
  @ViewChild('noteEditor') noteEditor!: NoteEditorComponent

  private _breaks: Break[] = []
  private _availability: CodeAvailability = { status: 'unavailable' }

  issuesCount = 0
  actionCodes: string[] = []
  descriptionCodes: string[] = []
  notesRequiredActionCodeSet = new Set<string>()
  breakIds: string[] = []
  modalTitle = 'Accept Break'
  saveBtnLabel = 'Save'
  newNote = ''
  noteHandling = BreakNoteHandling.REPLACE
  action = new FormControl(undefined)
  description = new FormControl(undefined)
  restrictedBreaks: string[] = []

  @Input() set availability(value: CodeAvailability) {
    this._availability = value
    this.actionCodes = actionCodeOptions(value)
    this.descriptionCodes = descriptionCodeOptions(value)
    this.notesRequiredActionCodeSet = notesRequiredActionCodes(value)
    this.updateActionFromBreaks()
    this.updateDescriptionFromBreaks()
    this.updateCodeValidators()
  }
  get availability(): CodeAvailability {
    return this._availability
  }

  // Make a configured-but-unselected code fail form validation (single break
  // only) so the Save button greys out, matching the required-note behavior.
  private updateCodeValidators() {
    const single = this._breaks.length === 1
    this.action.setValidators(
      single && this.actionCodes.length > 0 ? [Validators.required] : [],
    )
    this.description.setValidators(
      single && this.descriptionCodes.length > 0 ? [Validators.required] : [],
    )
    this.action.updateValueAndValidity()
    this.description.updateValueAndValidity()
  }

  get validation(): BreakDialogValidation {
    return breakDialogValidation({
      breaks: this.breaks,
      availability: this._availability,
      selectedActionCode: this.action.value ?? null,
      selectedDescriptionCode: this.description.value ?? null,
      note: this.newNote,
    })
  }

  get staleCodeHints(): string[] {
    return computeStaleCodeHints(this.breaks, this._availability)
  }

  @Input() set breaks(values: Break[]) {
    if (values.length === 0) {
      this.activeModal.dismiss()
    }

    this._breaks = values
    this.breakIds = values.map(b => b.identifier)

    this.updateActionFromBreaks()
    this.updateDescriptionFromBreaks()
    this.updateCodeValidators()

    if (values.length > 1) {
      this.modalTitle = `Accept Breaks (${values.length})`
      this.saveBtnLabel = `Save (${values.length})`
    }

    this.restrictedBreaks = this.breaks
      .filter(b => b.restrictedAction != null)
      .map(b => b.identifier)
  }
  get breaks(): Break[] {
    return this._breaks
  }

  formSubmitting = false
  form: FormGroup = new FormGroup({
    action: this.action,
    description: this.description,
  })

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    private readonly breaksService: MultiBreaksService,
    private readonly blankSessionService: BlankSessionService,
    private readonly store: Store<RootState>,
    private readonly breakCodeClipboard: BreakCodeClipboardService,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  onNoteEditorFormReady(noteEditorForm: FormGroup) {
    this.form.addControl('noteEditor', noteEditorForm)
  }

  onNoteHandlingChange(noteHandling: BreakNoteHandling) {
    this.noteHandling = noteHandling
  }

  async onSubmit() {
    if (this.validation.submitDisabled) return
    this.formSubmitting = true

    await this.blankSessionService.createBlankSessionIfNotInSession()

    this.breaksService
      .accept(
        this.breakIds,
        this.newNote,
        this.noteHandling,
        this.restrictedBreaks,
        this.action.value,
        this.description.value,
      )
      .pipe(take(1))
      .subscribe(
        response => {
          this.formSubmitting = false
          this.activeModal.close(response)
        },
        () => {
          this.formSubmitting = false
          this.toastService.error('Accept breaks failed, request cancelled.')
          this.activeModal.dismiss(null)
        },
      )
  }

  get canPaste(): boolean {
    return this.breakCodeClipboard.hasContent
  }

  /**
   * Fill the code + note boxes from the in-app clipboard. Codes go to THIS component's
   * `action`/`description`: NoteEditorComponent.form also carries action/description
   * controls this dialog never reads — writing those would silently do nothing.
   */
  onPasteBreakCodes(): void {
    const entry = this.breakCodeClipboard.read()
    if (!entry) {
      return
    }

    const result = resolvePaste(entry, this.actionCodes, this.descriptionCodes)
    this.action.setValue(result.actionCode)
    this.description.setValue(result.descriptionCode)
    this.noteEditor.form.controls.note.setValue(result.note)
    // Mirrors NoteEditorComponent's own invariant: a non-empty note must not sit in a
    // collapsed, invisible box, or the user saves a note the UI said wasn't there.
    if (result.note) {
      this.noteEditor.noteExpanded = true
    }

    if (result.error) {
      this.toastService.error(result.error)
    }
  }

  /** alt+v — see isPasteBreakCodesEvent for why this is not a hotkeys-registry binding. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!isPasteBreakCodesEvent(event, this.host.nativeElement)) {
      return
    }
    event.preventDefault()
    this.onPasteBreakCodes()
  }

  /**
   * Just close the modal and reset the form
   */
  onCancel() {
    this.activeModal.dismiss()
  }

  onNoteChange(newNote: string) {
    this.newNote = newNote
  }

  private updateActionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.actionCode),
      this.actionCodes,
    )
    if (value) this.action.setValue(value)
  }

  private updateDescriptionFromBreaks() {
    const value = prefillSelection(
      this._breaks.map(b => b.descriptionCode),
      this.descriptionCodes,
    )
    if (value) this.description.setValue(value)
  }
}
